import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as civilAdviseService from '../../../services/civilAdviseCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useCivilAdviseWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { useCivilAdviseLookups } from './useCivilAdviseLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { ReviewUploadedFilesTable } from '../../../components/wizard/ReviewUploadedFilesTable';
import { MultiFileReviewDocumentsTable } from '../civilAdvise/MultiFileReviewDocumentsTable';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { CivilAdviseWizardActions } from './CivilAdviseWizardActions';
import { civilAdviseWizardPath } from './CivilAdviseWizardShell';
import { isOtherCorporateInstitute } from './civilAdviseHelpers';
import { CIVIL_ADVISE_FIELD_LABELS as L } from './civilAdviseFieldLabels';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function CivilAdviseReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useCivilAdviseWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useCivilAdviseLookups('review', draft.provinceId);

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const connectedLabel = yesNoLabel(draft.hasConnectedMatters);

  const coverNoteFallback = useMemo(
    () => ({
      fileReferenceNumber: coverNoteFileRef || draft.referenceNo,
      fileCategory: draft.fileCategory,
      submittedDivisionName: profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '',
    }),
    [coverNoteFileRef, draft.referenceNo, draft.fileCategory, profile?.divisionUnit, profile?.externalAgencyName],
  );

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'civ-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await civilAdviseService.saveCivilAdviseRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = civilAdviseWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4) => civilAdviseWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        coverNoteFallback={coverNoteFallback}
        showSuccessBanner
        showCaseSource={false}
        onClose={closeCoverNote}
      />
      <div className="bc">
        <Link to={tasksListPath}>{roleCode === 'OFFICE_CLERK' ? t('nav.caseDetails') : t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>Create Case Request</Link>
        <span className="bc-sep">/</span>
        Review Before Submission
      </div>
      <div className="pg-title">Review Case Request Details</div>

      <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            <ReviewField label="Institute Type" value={instituteLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Intake Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label={L.hasPreviousAdvice} value={yesNoLabel(draft.hasPreviousAdvice)} />
            {draft.hasPreviousAdvice === 'yes' ? (
              <div className="ccr-review-field is-full">
                <div className="ccr-review-lbl">{L.previousAdviceAttachment}</div>
                <ReviewUploadedFilesTable
                  files={draft.previousAdviceFiles}
                  emptyMessage={t('common.noPreviousAdviceDocuments')}
                />
              </div>
            ) : null}
            <ReviewField label={L.connectedCaseRelevant} value={yesNoLabel(draft.hasConnectedCaseRelevant)} />
            {draft.hasConnectedCaseRelevant === 'yes' ? (
              <ReviewField label={L.connectedCaseDetails} value={draft.connectedCaseDetails} span={2} />
            ) : null}
            <ReviewField label={L.caseNumber} value={draft.caseNumber} />
            <ReviewField label={L.connectedMatters} value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label={L.connectedCaseNumber} value={draft.connectedCaseNumber} />
                <ReviewField label={L.connectedAgdFileRef} value={draft.connectedAgdFileRef} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Timeline" editTo={editTo(2)}>
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Submission & Physical File Delivery" editTo={editTo(3)}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted by" value={profile?.displayName?.trim() || '—'} />
            <ReviewField
              label="Submitted Division/ Unit"
              value={profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '—'}
            />
            <ReviewField label="Designation" value={profile?.contactDesignation?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={profile?.email?.trim() || '—'} />
            <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Upload Documents" editTo={editTo(4)}>
        <ReviewSubBlock>
          <MultiFileReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="civ-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              I confirm the above information is correct <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">Please confirm before submitting this request to AGD.</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <CivilAdviseWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={4}
        onBack={() => nav(editTo(4))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
